import {observer, Provider} from 'mobx-react';
import React, {useEffect, useMemo} from 'react';

import {AsyncPriceCourseCardContext} from '@udemy/browse-course';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {DiscoveryUnitsContainerStore, ValueProps} from '@udemy/react-discovery-units';
import {ContentArea} from '@udemy/react-layout-components';
import {CONTEXT_TYPES, ResourceContextMenuProvider} from '@udemy/resource-context-menu';
import {useUDData} from '@udemy/ud-data';

import {TopicPageExperimentVariants, TopicProps} from '../topic.react-component';
import {getPageObjectIdFromTopic} from '../utils';
import {
    TopicDiscoveryUnitsContainerWrapper,
    TopicDiscoveryUnitsContainerWrapperLoader,
} from './topic-discovery-units-wrapper.react-component';

/** Discovery API context for cert topic below-the-fold units (backend-defined). */
export const CERT_TOPIC_DISCOVERY_CONTEXT = 'topic_static_cert_voucher';
/**
 * Registered page type in @udemy/discovery-api (`topic_static`) — used for label_id
 * mapping and API validation. The cert-specific discovery context is overridden
 * via fetchOptions.context.
 */
export const CERT_TOPIC_DISCOVERY_PAGE_TYPE = 'topic_static';
export const CERT_TOPIC_DISCOVERY_FETCH_OPTIONS = {
    pageSize: 50,
    context: CERT_TOPIC_DISCOVERY_CONTEXT,
};
const RESOURCE_CONTEXT_MENU_PROPS = {context: CONTEXT_TYPES.ALL_COURSES};

export const filterCertTopicDiscoveryUnit = (unit: Partial<DiscoveryUnit>) =>
    unit.type !== 'discovery_value_props';

export interface CertificationTopicDiscoverySectionProps
    extends Pick<
            TopicProps,
            'topic' | 'isConsumerSubsSubscriber' | 'isSubsLoading' | 'enrollmentStat'
        >,
        TopicPageExperimentVariants {
    isMobile: boolean;
}

export const CertificationTopicDiscoverySection = observer(
    (props: CertificationTopicDiscoverySectionProps) => {
        const {
            topic,
            enrollmentStat,
            isMobile,
            isConsumerSubsSubscriber,
            isSubsLoading,
            showPersonalPlanBadge,
            useVerticalRecoCourseCardStyle,
            threeCardVerticalLayoutExperimentIsEnabled,
            topicSubscriptionUpsellFeature,
        } = props;

        const udData = useUDData();
        const isDiscoveryProviderReady = !isSubsLoading && !udData.isGlobalMeContextLoading;

        const mobxProviderProps = {
            isConsumerSubsSubscriber,
            showPersonalPlanBadge: showPersonalPlanBadge ?? false,
            showCodingExercisesBadge: false,
        };

        const verticalRecoCourseCardContextValue =
            useVerticalRecoCourseCardStyle === true
                ? {useCourseProductCard: true, hideCourseDescription: true}
                : {};

        const wrapTopicDiscoveryInContentArea =
            !!isMobile && !!threeCardVerticalLayoutExperimentIsEnabled;

        const pageObjectId = getPageObjectIdFromTopic(topic);

        const discoveryStore = useMemo(
            () =>
                new DiscoveryUnitsContainerStore({
                    pageType: CERT_TOPIC_DISCOVERY_PAGE_TYPE,
                    pageObjectId,
                }),
            [pageObjectId],
        );

        useEffect(
            function fetchCertTopicDiscoveryUnits() {
                if (!isDiscoveryProviderReady) {
                    return;
                }

                if (!discoveryStore.loading && discoveryStore.firstLoad && !discoveryStore.error) {
                    discoveryStore.fetchUnits(CERT_TOPIC_DISCOVERY_FETCH_OPTIONS);
                }
            },
            [discoveryStore, isDiscoveryProviderReady],
        );

        if (!isDiscoveryProviderReady || discoveryStore.firstLoad) {
            return <TopicDiscoveryUnitsContainerWrapperLoader isMobile={!!isMobile} />;
        }

        const topicDiscoveryUnits = (
            <TopicDiscoveryUnitsContainerWrapper
                topic={topic}
                enrollmentStat={enrollmentStat}
                isMobile={isMobile}
                topicSubscriptionUpsellFeature={topicSubscriptionUpsellFeature}
                store={discoveryStore}
                pageType={CERT_TOPIC_DISCOVERY_PAGE_TYPE}
                ppMerchandisingVariant="cert"
                discoveryUnitFilter={filterCertTopicDiscoveryUnit}
                fetchOptions={CERT_TOPIC_DISCOVERY_FETCH_OPTIONS}
            >
                <ValueProps className="component-margin" />
            </TopicDiscoveryUnitsContainerWrapper>
        );

        return (
            <Provider {...mobxProviderProps}>
                <ResourceContextMenuProvider resourceContextMenuProps={RESOURCE_CONTEXT_MENU_PROPS}>
                    <AsyncPriceCourseCardContext.Provider
                        value={verticalRecoCourseCardContextValue}
                    >
                        {wrapTopicDiscoveryInContentArea ? (
                            <ContentArea>{topicDiscoveryUnits}</ContentArea>
                        ) : (
                            topicDiscoveryUnits
                        )}
                    </AsyncPriceCourseCardContext.Provider>
                </ResourceContextMenuProvider>
            </Provider>
        );
    },
);
